import React from 'react';
import { Alert, Badge, Button, Form, Pagination, Spinner, Table } from 'react-bootstrap';

export const apiErrorMessage = (error, fallback = 'The request could not be completed.') => {
    const data = error?.response?.data;
    if (typeof data === 'string') return data || fallback;
    const validation = data?.errors ? Object.values(data.errors).flat().filter(Boolean) : [];
    return validation[0] || data?.detail || data?.title || data?.message || error?.message || fallback;
};

export const StatusBadge = ({ active }) => <Badge bg={active ? 'success' : 'secondary'}>{active ? 'Active' : 'Inactive'}</Badge>;

export const CapabilityBadge = ({ value }) => {
    const normalized = value || 'None';
    return <Badge bg={normalized === 'Execute' ? 'success' : normalized === 'View' ? 'primary' : 'secondary'}>{normalized}</Badge>;
};

export const Pager = ({ page, totalPages, onChange, disabled }) => totalPages > 1 && (
    <div className="company-access-pager">
        <Pagination size="sm" className="mb-0">
            <Pagination.First aria-label="First" disabled={disabled || page <= 1} onClick={() => onChange(1)} />
            <Pagination.Prev aria-label="Previous" disabled={disabled || page <= 1} onClick={() => onChange(page - 1)} />
            <Pagination.Next aria-label="Next" disabled={disabled || page >= totalPages} onClick={() => onChange(page + 1)} />
            <Pagination.Last aria-label="Last" disabled={disabled || page >= totalPages} onClick={() => onChange(totalPages)} />
        </Pagination>
        <span>Page <strong>{page}</strong> of <strong>{totalPages}</strong></span>
    </div>
);

const itemId = (type, item) => item[`${type}Id`];
const itemLabel = (type, item) => type === 'company' ? `${item.companyName} (${item.companyDb})`
    : type === 'role' ? (item.roleName || `Role ${item.roleId}`) : (item.displayName || item.email);
const selectionKey = selected => JSON.stringify([...selected.keys()].sort((a, b) => a - b));
const emptyText = type => type === 'company' ? 'No companies assigned.' : type === 'role' ? 'No roles assigned.' : 'No users assigned.';

export const PagedAssignmentSelector = React.forwardRef(function PagedAssignmentSelector(
    { type, assigned, loadOptions, onSave, readOnly, onDirtyChange, onSaved }, ref
) {
    const initial = React.useMemo(() => new Map(assigned.map(item => [itemId(type, item), item])), [assigned, type]);
    const [selected, setSelected] = React.useState(initial);
    const [original, setOriginal] = React.useState(() => selectionKey(initial));
    const [options, setOptions] = React.useState([]);
    const [searchInput, setSearchInput] = React.useState('');
    const [search, setSearch] = React.useState('');
    const [page, setPage] = React.useState(1);
    const [totalPages, setTotalPages] = React.useState(1);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [expanded, setExpanded] = React.useState(false);
    const dirty = selectionKey(selected) !== original;

    React.useEffect(() => { onDirtyChange?.(dirty, saving); }, [dirty, onDirtyChange, saving]);
    React.useEffect(() => {
        const timer = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
        return () => clearTimeout(timer);
    }, [searchInput]);
    React.useEffect(() => {
        let cancelled = false;
        setLoading(true); setError('');
        loadOptions({ search, includeInactive: false, page, pageSize: 50 }).then(result => {
            if (!cancelled) { setOptions(result.items || []); setTotalPages(Math.max(result.totalPages || 1, 1)); }
        }).catch(err => { if (!cancelled) setError(apiErrorMessage(err)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [loadOptions, page, search]);

    const toggle = item => {
        if (readOnly || saving) return;
        setSelected(current => {
            const next = new Map(current); const id = itemId(type, item);
            if (next.has(id)) next.delete(id); else if (item.isActive) next.set(id, item);
            return next;
        });
    };
    const save = React.useCallback(async () => {
        if (readOnly || saving || !dirty) return false;
        setSaving(true); setError('');
        try {
            const ids = [...selected.keys()].sort((a, b) => a - b);
            await onSave(ids);
            setOriginal(JSON.stringify(ids));
            onSaved?.([...selected.values()]);
            return true;
        } catch (err) { setError(apiErrorMessage(err)); return false; }
        finally { setSaving(false); }
    }, [dirty, onSave, onSaved, readOnly, saving, selected]);
    React.useImperativeHandle(ref, () => ({ save }), [save]);

    const selectedItems = [...selected.values()];
    const visibleSelected = expanded ? selectedItems : selectedItems.slice(0, 6);
    const remaining = Math.max(selectedItems.length - visibleSelected.length, 0);
    const title = type === 'company' ? 'Selected companies' : type === 'role' ? 'Selected roles' : 'Selected users';
    return <div className="company-assignment-selector">
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="assignment-selected">
            <div className="assignment-selected-heading"><strong>{title}</strong><span className="text-muted small">{selected.size} selected</span></div>
            {selected.size ? <div className="assignment-selected-list">
                {visibleSelected.map(item => <span className="assignment-selected-item" key={itemId(type, item)} title={itemLabel(type, item)}>
                    <span className="assignment-chip-label">{itemLabel(type, item)}</span>{!item.isActive && <Badge bg="secondary">Inactive</Badge>}
                    {!readOnly && <Button size="sm" variant="link" className="p-0" aria-label={`Remove ${itemLabel(type, item)}`} onClick={() => toggle(item)}>×</Button>}
                </span>)}
                {!expanded && remaining > 0 && <Button variant="link" size="sm" className="assignment-more" onClick={() => setExpanded(true)}>+{remaining} more</Button>}
                {expanded && selectedItems.length > 6 && <Button variant="link" size="sm" className="assignment-more" onClick={() => setExpanded(false)}>Show less</Button>}
            </div> : <div className="text-muted small mt-2">{emptyText(type)}</div>}
        </div>
        <Form.Control type="search" aria-label={`Search ${type} options`} placeholder={`Search ${type} options...`}
            value={searchInput} onChange={event => setSearchInput(event.target.value)} className="assignment-search" />
        <div className="company-access-table-shell assignment-table-shell">
            {loading ? <div className="company-access-state company-access-state-compact"><Spinner animation="border" size="sm" /><span>Loading options...</span></div> : options.length ?
                <Table responsive hover className="mb-0 align-middle company-access-table"><thead className="table-light"><tr>
                    <th className="assignment-use-column">Use</th>{type === 'user' ? <><th>Name</th><th>Email</th><th>Role</th></> : <th>{type === 'company' ? 'Company' : 'Role'}</th>}
                    <th className="assignment-status-column">Status</th></tr></thead><tbody>{options.map(item => {
                        const id = itemId(type, item); const checked = selected.has(id);
                        return <tr key={id}><td><Form.Check aria-label={`Select ${itemLabel(type, item)}`} checked={checked}
                            disabled={readOnly || (!item.isActive && !checked) || saving} onChange={() => toggle(item)} /></td>
                            {type === 'user' ? <><td>{item.displayName || <span className="text-muted">Not provided</span>}</td><td>{item.email}</td>
                                <td>{item.roleName || <span className="text-muted">No role</span>}</td></> : <td>{itemLabel(type, item)}</td>}
                            <td><StatusBadge active={item.isActive} /></td></tr>;
                    })}</tbody></Table> : <div className="company-access-empty">No matching options found.</div>}
        </div>
        <Pager {...{ page, totalPages, disabled: loading || saving, onChange: setPage }} />
        {readOnly && <p className="small text-muted mt-3 mb-0">Read-only access</p>}
    </div>;
});
